import type {
  PlaycastMessage,
  PlaycastMessageDisplayAddDisplay,
  PlaycastMessageDisplayRemoveDisplay,
  PlaycastMessageDisplayRemoveAllDisplays,
  PlaycastMessageDisplayGetDisplays,
  PlaycastMessageDisplayGetDriverStatus,
  PlaycastMessageDisplayPingDriver,
  PlaycastMessageDisplaySetRenderAdapter,
  PlaycastMessageDisplaySetActive,
  PlaycastMessageDisplayGetWatchdog,
  PlaycastMessageDisplayGetProtocolVersion,
  PlaycastMessageSource,
} from '../types';
import { formatMessage } from './_util/formatMessage';

const target = 'display';

export const addDisplay = (
  hostId: string,
  width: number,
  height: number,
  refreshRate: number,
  deviceName: string,
  serialNumber: string,
  applyAppendSecondaryToActiveSession: boolean,
  guid?: string,
  userAuth?: string,
  preserveHostPrimary?: boolean,
  sessionId?: string,
  quiesceAfterCreate?: boolean,
): PlaycastMessage<PlaycastMessageDisplayAddDisplay> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageDisplayAddDisplay = {
    target,
    action: 'addDisplay',
    isReply: false,
    message: {
      width,
      height,
      refreshRate,
      deviceName,
      serialNumber,
      applyAppendSecondaryToActiveSession,
      guid,
      ...(preserveHostPrimary !== undefined ? { preserveHostPrimary } : {}),
      ...(sessionId !== undefined ? { sessionId } : {}),
      ...(quiesceAfterCreate !== undefined ? { quiesceAfterCreate } : {}),
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageDisplayAddDisplay>;
};

export const removeDisplay = (
  hostId: string,
  guid: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageDisplayRemoveDisplay> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageDisplayRemoveDisplay = {
    target,
    action: 'removeDisplay',
    isReply: false,
    message: {
      guid,
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageDisplayRemoveDisplay>;
};

export const removeAllDisplays = (
  hostId: string,
  useOrderedSecondaryRemoval: boolean,
  removeAllVirtualDisplaysInDriver: boolean,
  restoreSavedHostBaselineAfter: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageDisplayRemoveAllDisplays> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageDisplayRemoveAllDisplays = {
    target,
    action: 'removeAllDisplays',
    isReply: false,
    message: {
      useOrderedSecondaryRemoval,
      removeAllVirtualDisplaysInDriver,
      restoreSavedHostBaselineAfter,
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageDisplayRemoveAllDisplays>;
};

export const getDisplays = (
  hostId: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageDisplayGetDisplays> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageDisplayGetDisplays = {
    target,
    action: 'getDisplays',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageDisplayGetDisplays>;
};

export const getDriverStatus = (
  hostId: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageDisplayGetDriverStatus> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageDisplayGetDriverStatus = {
    target,
    action: 'getDriverStatus',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageDisplayGetDriverStatus>;
};

export const pingDriver = (
  hostId: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageDisplayPingDriver> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageDisplayPingDriver = {
    target,
    action: 'pingDriver',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageDisplayPingDriver>;
};

export const setRenderAdapter = (
  hostId: string,
  adapterLuid: { lowPart: number; highPart: number },
  userAuth?: string,
): PlaycastMessage<PlaycastMessageDisplaySetRenderAdapter> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageDisplaySetRenderAdapter = {
    target,
    action: 'setRenderAdapter',
    isReply: false,
    message: {
      adapterLuid,
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageDisplaySetRenderAdapter>;
};

export const setActive = (
  hostId: string,
  activation: PlaycastMessageDisplaySetActive['message'],
  userAuth?: string,
): PlaycastMessage<PlaycastMessageDisplaySetActive> => {
  const message: PlaycastMessageDisplaySetActive = {
    target,
    action: 'setActive',
    isReply: false,
    message: activation,
  };
  return formatMessage(hostId, 'host', message, userAuth) as PlaycastMessage<PlaycastMessageDisplaySetActive>;
};

export const getWatchdog = (
  hostId: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageDisplayGetWatchdog> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageDisplayGetWatchdog = {
    target,
    action: 'getWatchdog',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageDisplayGetWatchdog>;
};

export const getProtocolVersion = (
  hostId: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageDisplayGetProtocolVersion> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageDisplayGetProtocolVersion = {
    target,
    action: 'getProtocolVersion',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageDisplayGetProtocolVersion>;
};

